// https://github.com/colinhacks/zod/blob/62b7842c46dd7f1004f41e2220284abb75378fb7/src/locales/en.ts

import {
  ZodErrorMap,
  ZodIssueCode,
  ZodParsedType,
  util,
} from 'zod';

const errorMap: ZodErrorMap = (issue, _ctx) => {
  let message: string;
  switch (issue.code) {
    case ZodIssueCode.invalid_type:
      if (issue.received === ZodParsedType.undefined) {
        message = 'Povinné';
      } else {
        message = `Očakávalo sa ${issue.expected}, dostalo sa ${issue.received}`;
      }
      break;
    case ZodIssueCode.invalid_literal:
      message = `Neplatná hodnota literálu, očakávalo sa ${JSON.stringify(
        issue.expected,
        util.jsonStringifyReplacer,
      )}`;
      break;
    case ZodIssueCode.unrecognized_keys:
      message = `Nerozpoznaný/é kľúč/e v objekte: ${util.joinValues(
        issue.keys,
        ', ',
      )}`;
      break;
    case ZodIssueCode.invalid_union:
      message = 'Neplatné vstupné údaje';
      break;
    case ZodIssueCode.invalid_union_discriminator:
      message = `Neplatná hodnota diskriminátora. Očakávalo sa ${util.joinValues(
        issue.options,
      )}`;
      break;
    case ZodIssueCode.invalid_enum_value:
      message = `Neplatná hodnota výčtu. Očakávalo sa ${util.joinValues(
        issue.options,
      )}, dostalo sa '${issue.received}'`;
      break;
    case ZodIssueCode.invalid_arguments:
      message = 'Neplatné argumenty funkcie';
      break;
    case ZodIssueCode.invalid_return_type:
      message = 'Neplatný návratový typ funkcie';
      break;
    case ZodIssueCode.invalid_date:
      message = 'Neplatný dátum';
      break;
    case ZodIssueCode.invalid_string:
      if (typeof issue.validation === 'object') {
        if ('includes' in issue.validation) {
          message = `Neplatné údaje: musí obsahovať "${issue.validation.includes}"`;

          if (typeof issue.validation.position === 'number') {
            message = `${message} na jednej alebo viacerých pozíciách väčších alebo rovných ${issue.validation.position}`;
          }
        } else if ('startsWith' in issue.validation) {
          message = `Neplatné údaje: musí začínať s "${issue.validation.startsWith}"`;
        } else if ('endsWith' in issue.validation) {
          message = `Neplatné údaje: musí končiť s "${issue.validation.endsWith}"`;
        } else {
          util.assertNever(issue.validation);
        }
      } else if (issue.validation !== 'regex') {
        message = `Neplatné ${issue.validation}`;
      } else {
        message = 'Neplatné';
      }
      break;
    case ZodIssueCode.too_small:
      if (issue.type === 'array') {
        message = `Pole musí obsahovať ${
          issue.exact ? 'presne' : issue.inclusive ? 'aspoň' : 'viac ako'
        } ${issue.minimum} prvok(y)`;
      } else if (issue.type === 'string') {
        if (issue.inclusive && issue.minimum === 1) {
          message = 'Nesmie byť prázdne';
        } else {
          message = `Reťazec musí obsahovať ${
            issue.exact ? 'presne' : issue.inclusive ? 'aspoň' : 'viac ako'
          } ${issue.minimum} znakov`;
        }
      } else if (issue.type === 'number') {
        message = `Číslo musí byť ${
          issue.exact
            ? 'presne rovné '
            : issue.inclusive
              ? 'väčšie alebo rovné '
              : 'väčšie ako '
        }${issue.minimum}`;
      } else if (issue.type === 'date') {
        message = `Dátum musí byť ${
          issue.exact
            ? 'presne rovný '
            : issue.inclusive
              ? 'rovnaký alebo neskorší ako '
              : 'neskorší ako '
        }${new Date(Number(issue.minimum))}`;
      } else {
        message = 'Neplatné údaje';
      }
      break;
    case ZodIssueCode.too_big:
      if (issue.type === 'array') {
        message = `Pole musí obsahovať ${
          issue.exact ? 'presne' : issue.inclusive ? 'maximálne' : 'menej ako'
        } ${issue.maximum} prvok(y)`;
      } else if (issue.type === 'string') {
        message = `Reťazec musí obsahovať ${
          issue.exact ? 'presne' : issue.inclusive ? 'maximálne' : 'menej ako'
        } ${issue.maximum} znakov`;
      } else if (issue.type === 'number') {
        message = `Číslo musí byť ${
          issue.exact
            ? 'presne'
            : issue.inclusive
              ? 'menšie alebo rovné'
              : 'menšie ako'
        } ${issue.maximum}`;
      } else if (issue.type === 'bigint') {
        message = `BigInt musí byť ${
          issue.exact
            ? 'presne'
            : issue.inclusive
              ? 'menší alebo rovný'
              : 'menší ako'
        } ${issue.maximum}`;
      } else if (issue.type === 'date') {
        message = `Dátum musí byť ${
          issue.exact
            ? 'presne'
            : issue.inclusive
              ? 'rovnaký alebo skorší ako'
              : 'skorší ako'
        } ${new Date(Number(issue.maximum))}`;
      } else {
        message = 'Neplatné údaje';
      }
      break;
    case ZodIssueCode.custom:
      message = 'Neplatné údaje';
      break;
    case ZodIssueCode.invalid_intersection_types:
      message = 'Výsledky prieniku sa nepodarilo spojiť';
      break;
    case ZodIssueCode.not_multiple_of:
      message = `Číslo musí byť násobkom ${issue.multipleOf}`;
      break;
    case ZodIssueCode.not_finite:
      message = 'Číslo musí byť konečné';
      break;
    default:
      message = _ctx.defaultError;
      util.assertNever(issue);
  }
  return {
    message,
  };
};

export default errorMap;
